import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Tooltip,
  Skeleton,
  Alert,
  Pagination,
  SelectChangeEvent,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search,
  School,
  Insights,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { coursesAPI } from "../../services/adminApi";

interface Course {
  _id: string;
  title: string;
  titleAr?: string;
  description: string;
  descriptionAr?: string;
  thumbnail: string;
  status: "draft" | "published" | "archived";
  phases: any[];
  createdAt: string;
  updatedAt: string;
}

/** At-a-glance flag so admins can spot courses still missing Arabic. */
const ArabicStatusChip: React.FC<{ course: Course }> = ({ course }) => {
  const translated = !!course.titleAr?.trim();
  return (
    <Chip
      label={translated ? "AR ✓" : "AR —"}
      size="small"
      title={
        translated
          ? "Arabic title provided"
          : "No Arabic title — members browsing in Arabic will see the English one"
      }
      sx={{
        height: 20,
        fontSize: "0.65rem",
        fontWeight: 700,
        bgcolor: translated ? "#ECFDF5" : "rgba(0,0,0,0.05)",
        color: translated ? "#10B981" : "#9CA3AF",
      }}
    />
  );
};

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface CourseUserProgressRow {
  userId: string;
  name: string;
  email: string;
  completedCount: number;
  totalItems: number;
  progressPercentage: number;
  likedCourse: boolean;
  isLocked: boolean;
  lockReason?: string;
  lastActivityAt?: string | null;
  learningStatus: "not_started" | "in_progress" | "completed" | "halted";
  currentPosition: string;
}

interface CourseUserProgressSummary {
  totalUsers: number;
  startedUsers: number;
  inProgressUsers: number;
  completedUsers: number;
  haltedUsers: number;
  lockedUsers: number;
  totalComments: number;
  totalLikes: number;
  courseLikes: number;
  averageProgress: number;
}

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  published: { bg: "#ECFDF5", color: "#10B981" },
  draft: { bg: "#FFF7ED", color: "#F59E0B" },
  archived: { bg: "rgba(203, 184, 168, 0.15)", color: "#6B7280" },
};

const AdminCourses: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<Course | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Tracking dialog
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [trackingCourse, setTrackingCourse] = useState<Course | null>(null);
  const [trackingRows, setTrackingRows] = useState<CourseUserProgressRow[]>([]);
  const [trackingSummary, setTrackingSummary] = useState<CourseUserProgressSummary | null>(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [lockSavingUserId, setLockSavingUserId] = useState<string | null>(null);
  const [lockReasonDrafts, setLockReasonDrafts] = useState<Record<string, string>>({});

  const loadCourses = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params: any = { page: pagination.page, limit: pagination.limit };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;

      const res = await coursesAPI.list(params);
      setCourses(res.data.courses);
      setPagination(res.data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load courses");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, statusFilter]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const handleStatusChange = (e: SelectChangeEvent) => {
    setStatusFilter(e.target.value);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const handlePageChange = (_: any, page: number) => {
    setPagination((p) => ({ ...p, page }));
  };

  const handleDeleteClick = (course: Course) => {
    setCourseToDelete(course);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!courseToDelete) return;
    try {
      setDeleting(true);
      await coursesAPI.delete(courseToDelete._id);
      setDeleteDialogOpen(false);
      setCourseToDelete(null);
      loadCourses();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to delete course");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const loadCourseTracking = useCallback(async (course: Course) => {
    try {
      setTrackingLoading(true);
      const res = await coursesAPI.getUserProgress(course._id, { limit: 100, page: 1 });
      setTrackingRows(res.data.users || []);
      setTrackingSummary(res.data.summary || null);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load course tracking");
    } finally {
      setTrackingLoading(false);
    }
  }, []);

  const openTrackingDialog = (course: Course) => {
    setTrackingCourse(course);
    setTrackingOpen(true);
    setTrackingRows([]);
    setTrackingSummary(null);
    setLockReasonDrafts({});
    loadCourseTracking(course);
  };

  const toggleUserLock = async (row: CourseUserProgressRow, nextLocked: boolean) => {
    if (!trackingCourse) return;
    try {
      setLockSavingUserId(row.userId);
      const lockReason = lockReasonDrafts[row.userId] ?? row.lockReason ?? "";
      await coursesAPI.toggleUserLock(trackingCourse._id, row.userId, {
        isLocked: nextLocked,
        lockReason,
      });

      setTrackingRows((prev) =>
        prev.map((r) =>
          r.userId === row.userId
            ? {
                ...r,
                isLocked: nextLocked,
                lockReason: nextLocked ? lockReason : "",
              }
            : r
        )
      );
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to update lock state");
    } finally {
      setLockSavingUserId(null);
    }
  };

  // â”€â”€â”€ Skeleton Loading â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const renderSkeleton = () => (
    <Box>
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          height={60}
          sx={{ mb: 1, borderRadius: 1 }}
        />
      ))}
    </Box>
  );

  // â”€â”€â”€ Mobile Card View â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const renderMobileCards = () => (
    <Stack spacing={1.5}>
      {courses.map((course) => {
        const statusStyle = STATUS_STYLES[course.status] || STATUS_STYLES.draft;
        return (
          <Card
            key={course._id}
            elevation={0}
            sx={{
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: "12px",
            }}
          >
            <CardContent sx={{ pb: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                  <School sx={{ color: "#1565C0", fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600} noWrap>
                    {course.title}
                  </Typography>
                  <ArabicStatusChip course={course} />
                </Box>
                <Chip
                  label={course.status}
                  size="small"
                  sx={{
                    bgcolor: statusStyle.bg,
                    color: statusStyle.color,
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    height: 24,
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                {course.phases?.length || 0} phases Â· Created{" "}
                {formatDate(course.createdAt)}
              </Typography>
              {course.titleAr?.trim() && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  dir="rtl"
                  sx={{ display: "block", mb: 0.5, fontFamily: "'Cairo', 'Segoe UI', sans-serif" }}
                >
                  {course.titleAr}
                </Typography>
              )}
              {course.description && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {course.description}
                </Typography>
              )}
            </CardContent>
            <CardActions sx={{ px: 2, pb: 1.5, pt: 0 }}>
              <Button
                size="small"
                startIcon={<Edit />}
                onClick={() => navigate(`/admin/courses/${course._id}`)}
                sx={{ color: "#1565C0" }}
              >
                Edit
              </Button>
              <Button
                size="small"
                startIcon={<Insights />}
                onClick={() => openTrackingDialog(course)}
                sx={{ color: "#2563EB" }}
              >
                Track
              </Button>
              <Button
                size="small"
                startIcon={<Delete />}
                onClick={() => handleDeleteClick(course)}
                sx={{ color: "#EF4444" }}
              >
                Delete
              </Button>
            </CardActions>
          </Card>
        );
      })}
    </Stack>
  );

  // â”€â”€â”€ Desktop Table View â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const renderTable = () => (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: "rgba(0,0,0,0.02)" }}>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#6B7280" }}>
              Course
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#6B7280" }}>
              Status
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#6B7280" }}>
              Phases
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#6B7280" }}>
              Created
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#6B7280" }}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {courses.map((course) => {
            const statusStyle =
              STATUS_STYLES[course.status] || STATUS_STYLES.draft;
            return (
              <TableRow
                key={course._id}
                hover
                sx={{
                  "&:hover": { bgcolor: "rgba(216, 154, 122, 0.04)" },
                  cursor: "pointer",
                }}
                onClick={() => navigate(`/admin/courses/${course._id}`)}
              >
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {course.thumbnail ? (
                      <Box
                        component="img"
                        src={course.thumbnail}
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "8px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "8px",
                          bgcolor: "rgba(216, 154, 122, 0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <School sx={{ color: "#1565C0", fontSize: 24 }} />
                      </Box>
                    )}
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {course.title}
                        </Typography>
                        <ArabicStatusChip course={course} />
                      </Box>
                      {course.titleAr?.trim() && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          dir="rtl"
                          sx={{ display: "block", fontFamily: "'Cairo', 'Segoe UI', sans-serif" }}
                        >
                          {course.titleAr}
                        </Typography>
                      )}
                      {course.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            maxWidth: 300,
                          }}
                        >
                          {course.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={course.status}
                    size="small"
                    sx={{
                      bgcolor: statusStyle.bg,
                      color: statusStyle.color,
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 26,
                      textTransform: "capitalize",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {course.phases?.length || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(course.createdAt)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/courses/${course._id}`);
                      }}
                      sx={{ color: "#1565C0" }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(course);
                      }}
                      sx={{ color: "#EF4444" }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Track users">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openTrackingDialog(course);
                      }}
                      sx={{ color: "#2563EB" }}
                    >
                      <Insights fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: "#0A1628" }}
          >
            Courses
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your courses and their content
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate("/admin/courses/new")}
          sx={{
            bgcolor: "#1565C0",
            "&:hover": { bgcolor: "#C48A6A" },
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
          }}
        >
          Create Course
        </Button>
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: "12px",
          border: "1px solid rgba(0,0,0,0.08)",
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder="Search courses..."
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "#9CA3AF", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            minWidth: 200,
            "& .MuiOutlinedInput-root": { borderRadius: "10px" },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={handleStatusChange}
            sx={{ borderRadius: "10px" }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="published">Published</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
          {error}
        </Alert>
      )}

      {/* Content */}
      {loading ? (
        renderSkeleton()
      ) : courses.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: "12px",
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <School sx={{ fontSize: 48, color: "#D1D5DB", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No courses yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first course to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/admin/courses/new")}
            sx={{
              bgcolor: "#1565C0",
              "&:hover": { bgcolor: "#C48A6A" },
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Create Course
          </Button>
        </Paper>
      ) : (
        <>
          {isMobile ? renderMobileCards() : renderTable()}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="standard"
                sx={{
                  "& .Mui-selected": {
                    bgcolor: "#1565C0 !important",
                    color: "#fff",
                  },
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: "14px", maxWidth: 400 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Delete Course
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete{" "}
            <strong>{courseToDelete?.title}</strong>? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ borderRadius: "8px", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={deleting}
            sx={{
              bgcolor: "#EF4444",
              "&:hover": { bgcolor: "#DC2626" },
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tracking Dialog */}
      <Dialog
        open={trackingOpen}
        onClose={() => setTrackingOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          User Progress Tracking {trackingCourse ? `- ${trackingCourse.title}` : ""}
        </DialogTitle>
        <DialogContent>
          {trackingLoading ? (
            <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Box>
          ) : trackingRows.length === 0 ? (
            <Alert severity="info">No users found yet.</Alert>
          ) : (
            <>
              {trackingSummary && (
                <Box
                  sx={{
                    mb: 2,
                    display: "grid",
                    gap: 1.2,
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(4, minmax(0, 1fr))",
                      lg: "repeat(10, minmax(0, 1fr))",
                    },
                  }}
                >
                  {[
                    { label: "Users", value: trackingSummary.totalUsers },
                    { label: "Started", value: trackingSummary.startedUsers },
                    { label: "In Progress", value: trackingSummary.inProgressUsers },
                    { label: "Completed", value: trackingSummary.completedUsers },
                    { label: "Halted", value: trackingSummary.haltedUsers },
                    { label: "Locked", value: trackingSummary.lockedUsers },
                    { label: "Comments", value: trackingSummary.totalComments },
                    { label: "Comment Likes", value: trackingSummary.totalLikes },
                    { label: "Course Likes", value: trackingSummary.courseLikes },
                    { label: "Avg Progress", value: `${trackingSummary.averageProgress}%` },
                  ].map((card) => (
                    <Paper
                      key={card.label}
                      elevation={0}
                      sx={{
                        p: 1.2,
                        borderRadius: "10px",
                        border: "1px solid rgba(0,0,0,0.08)",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {card.label}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {card.value}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}

              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: "10px" }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "rgba(0,0,0,0.02)" }}>
                      <TableCell>User</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Progress</TableCell>
                      <TableCell>Completed</TableCell>
                      <TableCell>Liked Course</TableCell>
                      <TableCell>Current Position</TableCell>
                      <TableCell>Last Activity</TableCell>
                      <TableCell>Lock</TableCell>
                      <TableCell>Lock Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trackingRows.map((row) => (
                      <TableRow key={row.userId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {row.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.learningStatus.replace("_", " ")}
                            sx={{
                              textTransform: "capitalize",
                              bgcolor:
                                row.learningStatus === "completed"
                                  ? "#ECFDF5"
                                  : row.learningStatus === "in_progress"
                                  ? "#EFF6FF"
                                  : row.learningStatus === "halted"
                                  ? "#FEF2F2"
                                  : "#F3F4F6",
                              color:
                                row.learningStatus === "completed"
                                  ? "#10B981"
                                  : row.learningStatus === "in_progress"
                                  ? "#2563EB"
                                  : row.learningStatus === "halted"
                                  ? "#DC2626"
                                  : "#6B7280",
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={`${row.progressPercentage}%`}
                            sx={{
                              bgcolor:
                                row.progressPercentage >= 80
                                  ? "#ECFDF5"
                                  : row.progressPercentage >= 40
                                  ? "#FFF7ED"
                                  : "#FEF2F2",
                              color:
                                row.progressPercentage >= 80
                                  ? "#10B981"
                                  : row.progressPercentage >= 40
                                  ? "#F59E0B"
                                  : "#EF4444",
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>
                        <TableCell>{row.completedCount}/{row.totalItems}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.likedCourse ? "Yes" : "No"}
                            sx={{
                              bgcolor: row.likedCourse ? "#FEF2F2" : "#F3F4F6",
                              color: row.likedCourse ? "#DC2626" : "#6B7280",
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {row.currentPosition || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.lastActivityAt
                            ? formatDate(row.lastActivityAt)
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={row.isLocked}
                                disabled={lockSavingUserId === row.userId}
                                onChange={(_, checked) => toggleUserLock(row, checked)}
                                color="warning"
                              />
                            }
                            label={row.isLocked ? "Locked" : "Unlocked"}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Reason (optional)"
                            value={lockReasonDrafts[row.userId] ?? row.lockReason ?? ""}
                            onChange={(e) =>
                              setLockReasonDrafts((prev) => ({
                                ...prev,
                                [row.userId]: e.target.value,
                              }))
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTrackingOpen(false)} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCourses;


